import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { InputField } from '../../components/InputField';
import { SetupLayout } from '../../components/SetupLayout';
import { useSetup, SetupDeviceType } from '../../store/setupStore';
import { useAuth } from '../../store/authStore';
import { registerDevice } from '../../api/devices';
function BeltIllustration() {
  return <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-2166" data-mp="1ksf1kszwubol">
      <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-2167" data-mp="1fwzpa5hjwnua" />
      <path d="M40 100 C40 60, 160 60, 160 100 C160 140, 40 140, 40 100 Z" fill="#6B21E8" opacity="0.8" data-id="element-2168" data-mp="1tn38sop1q3g1" />
      <circle cx="70" cy="100" r="12" fill="#FFFFFF" data-id="element-2169" data-mp="1ovbo2uskmrxt" />
      <circle cx="100" cy="100" r="16" fill="#FFFFFF" data-id="element-2170" data-mp="116sgkhhcij6s" />
      <circle cx="130" cy="100" r="12" fill="#FFFFFF" data-id="element-2171" data-mp="115hagb0fgzci" />
      <path d="M100 60 Q120 40 140 60" stroke="#00D4D4" strokeWidth="3" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2172" data-mp="x5glvxjm77rq" />
      <path d="M100 140 Q120 160 140 140" stroke="#00D4D4" strokeWidth="3" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2173" data-mp="ghtdach5dmgr" />
    </svg>;
}
function CradleIllustration() {
  return <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-2174" data-mp="q6bbqn1yfhh8">
      <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-2175" data-mp="3opi8yhjly9yd" />
      <path d="M50 100 Q100 70 150 100 L150 130 Q100 145 50 130 Z" fill="#6B21E8" opacity="0.85" data-id="element-2176" data-mp="3or31y2q9xku4" />
      <path d="M40 132 Q100 160 160 132" stroke="#6B21E8" strokeWidth="4" fill="none" strokeLinecap="round" data-id="element-2177" data-mp="2i1ttw7v3dvd" />
      <line x1="70" y1="90" x2="70" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2178" data-mp="7hwng7j0uhk4" />
      <line x1="90" y1="84" x2="90" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2179" data-mp="1ivs9m08cfqku" />
      <line x1="110" y1="84" x2="110" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2180" data-mp="3f19g9l9tu348" />
      <line x1="130" y1="90" x2="130" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2181" data-mp="3nzl3refecksg" />
      <circle cx="100" cy="55" r="14" fill="#FFFFFF" data-id="element-2182" data-mp="2w85k9t94ze6g" />
      <circle cx="100" cy="55" r="8" fill="#6B21E8" data-id="element-2183" data-mp="2w6uj8172skj1" />
      <circle cx="100" cy="55" r="3" fill="#00D4D4" data-id="element-2184" data-mp="2rax0jo8cb233" />
      <line x1="100" y1="69" x2="100" y2="78" stroke="#6B21E8" strokeWidth="3" strokeLinecap="round" data-id="element-2185" data-mp="2r812lzwhsden" />
      <path d="M120 45 Q130 38 138 45" stroke="#00D4D4" strokeWidth="2.5" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2186" data-mp="308z8youekx0e" />
      <path d="M125 38 Q135 28 145 38" stroke="#00D4D4" strokeWidth="2.5" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2187" data-mp="28ikyz3pig4u0" />
    </svg>;
}
export function DeviceEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    token
  } = useAuth();
  const {
    setDeviceType,
    deviceType,
    labels,
    setProvisioningData
  } = useSetup();
  const [uid, setUid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = searchParams.get('type');
    if (t === 'cradle' || t === 'belt') {
      setDeviceType(t as SetupDeviceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isCradle = deviceType === 'cradle';
  const handleLink = async () => {
    if (!uid.trim()) {
      setError('Please enter a Device UID');
      return;
    }
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const formattedUid = uid.trim().toUpperCase();
      const res = await registerDevice(token, formattedUid);
      setProvisioningData({
        deviceUid: formattedUid,
        deviceId: res.deviceId,
        provisioningToken: res.provisioningToken,
        expiresAt: res.expiresAt
      });
      navigate('/app/setup/waiting');
    } catch (err: any) {
      setError(err.message || `Couldn't link this ${labels.singular}. Please check the code and try again.`);
    } finally {
      setIsLoading(false);
    }
  };
  return <SetupLayout step={1} onBack={() => navigate('/app/profile/devices')} data-id="element-2188" data-mp="1uzmjfbrn8pny">
      <div className="flex flex-col items-center flex-1 justify-center" data-id="element-2189" data-mp="1pz8alri7xbov">
        <div className="w-full max-w-[160px] aspect-square mb-6 flex items-center justify-center" data-id="element-2190" data-mp="1q1b8sytiyb4o">
          {isCradle ? <CradleIllustration data-id="element-2191" data-mp="ekbk0d6dyefe" /> : <BeltIllustration data-id="element-2192" data-mp="eklae1vcenlm" />}
        </div>

        <h1 className="text-[26px] font-semibold text-text-primary text-center mb-3" data-id="element-2193" data-mp="elmn0as0bi5g">
          Link your {labels.singular}
        </h1>

        <p className="text-[16px] text-text-secondary text-center max-w-[320px] mb-8" data-id="element-2194" data-mp="2l8vf5k4lvpcq">
          Enter the Device UID printed on the inside of your {labels.singular}'s
          box.
        </p>

        <div className="w-full max-w-[320px] space-y-6 mb-auto" data-id="element-2195" data-mp="2v71fhxarknfh">
          <div data-id="element-2196" data-mp="2v5gg9udi8gba">
            <label htmlFor="deviceUid" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-2197" data-mp="2v4ydeewc73ue">
              Device UID
            </label>
            <InputField id="deviceUid" placeholder="e.g. ZGA9CZ" value={uid} onChange={e => {
            setUid(e.target.value.toUpperCase());
            if (error) setError(null);
          }} maxLength={12} autoComplete="off" data-id="element-2198" data-mp="34ynpjqbd8ggn" />
          </div>

          {error && <p className="text-[14px] text-danger text-center animate-fadeInUp" data-id="element-2199" data-mp="1jl2pi1lm6cf3">
              {error}
            </p>}

          <Button onClick={handleLink} isLoading={isLoading} disabled={!uid.trim()} data-id="element-2200" data-mp="1ghn5o2q83glt">
            Link {labels.titleCase}
          </Button>
        </div>
      </div>
    </SetupLayout>;
}